import { useEffect, useRef, useState } from "react";

export const PAGE_W = 794;
export const PAGE_H = 1123;

export const pageCount = (h) => Math.max(1, Math.ceil((h - 4) / PAGE_H));

// Dashed line inside the sheet (unscaled coordinates).
const BreakLine = ({ n }) => (
  <div className="pointer-events-none absolute left-0 right-0 z-10 border-t-2 border-dashed border-[#94A3B8]/60"
    style={{ top: (n - 1) * PAGE_H }} data-testid={`page-break-${n}`} />
);

// Fixed-size page number sitting on the sheet's right edge, outside the text area.
const BreakBadge = ({ n, top }) => (
  <span className="pointer-events-none absolute right-0 z-20 flex h-[18px] min-w-[18px] -translate-y-1/2 translate-x-1/2 items-center justify-center rounded-full border border-[#94A3B8] bg-white px-1 text-[10px] font-semibold leading-none text-[#475569] shadow-sm"
    style={{ top }} title={`Page ${n} starts about here`} data-testid={`page-break-label-${n}`}>
    {n}
  </span>
);

export const PaperPreview = ({ children, watermark, testId = "paper-preview", maxScale = 1, pageBreaks = false, onPages, footer }) => {
  const outer = useRef(null);
  const inner = useRef(null);
  const [scale, setScale] = useState(0.3);
  const [h, setH] = useState(PAGE_H);
  const pages = pageCount(h);
  const breaks = pageBreaks ? Array.from({ length: pages - 1 }, (_, i) => i + 2) : [];

  useEffect(() => { onPages?.(pages); }, [pages, onPages]);

  useEffect(() => {
    const ro = new ResizeObserver(() => {
      if (outer.current) setScale(Math.min(maxScale, outer.current.clientWidth / PAGE_W));
      if (inner.current) setH(inner.current.offsetHeight);
    });
    ro.observe(outer.current);
    ro.observe(inner.current);
    return () => ro.disconnect();
  }, [maxScale]);

  return (
    <div ref={outer} className="w-full min-w-0" data-testid={testId}>
      <div className="relative mx-auto" style={{ width: PAGE_W * scale, height: h * scale }}>
        <div
          ref={inner}
          className="absolute left-0 top-0 origin-top-left bg-white shadow-[0_1px_2px_rgba(15,23,42,.06),0_18px_40px_-12px_rgba(15,23,42,.18)]"
          style={{ width: PAGE_W, minHeight: PAGE_H, transform: `scale(${scale})` }}
        >
          {children}
          {breaks.map((n) => <BreakLine key={n} n={n} />)}
          {watermark && (
            <>
              <div className="watermark-pattern pointer-events-none absolute inset-0" data-testid="preview-watermark" />
              <div className="absolute bottom-0 left-0 right-0 bg-[#0F172A] py-2 text-center font-mono text-[11px] uppercase tracking-[0.2em] text-white/80">
                Made with Pitchpaper · free preview
              </div>
            </>
          )}
        </div>
        {breaks.map((n) => <BreakBadge key={n} n={n} top={(n - 1) * PAGE_H * scale} />)}
      </div>
      {pageBreaks && (
        <p className={`mt-3 text-center text-xs ${pages > 2 ? "text-amber-700" : "text-taupe"}`} data-testid="page-count">
          About {pages} {pages === 1 ? "page" : "pages"}
          {pages > 1 && " · dashed lines show roughly where each new page starts"}
          {pages > 2 && " · most recruiters prefer one or two"}
        </p>
      )}
      {footer && <div className="flex justify-center">{footer}</div>}
    </div>
  );
};
